import { CreateFYIEntitiesResponse, CreateFYIEntity } from '../../../../../../../queries/use-create-fyi-entities';
import { EntityTypeEnum } from '../../../../../../../types/entity';
import { OnboardingFormEntry } from './FYIEntitiesOnboarding';
import plural from '../../../../../../../utilities/plural';

// Format input field error (for rows without day/month values) before sending request to create FYI entities
export function formatEntityRowsError(selectedEntities: OnboardingFormEntry[]): {
    entitiesWithoutDayAndMonthValues: number[];
    entitiesWithoutDayValues: number[];
} {
    const errorEntities = selectedEntities.reduce(function (
        acc: { dayMonthError?: number; dayError?: number }[],
        curr: OnboardingFormEntry,
        i: number
    ) {
        if (curr.isSelected && !curr.month && !curr.day) {
            acc.push({ dayMonthError: i });
        }
        if (curr.isSelected && curr.month && !curr.day) {
            acc.push({ dayError: i });
        }
        return acc;
    },
    []);

    const entitiesWithoutDayAndMonthValues = errorEntities
        .map((err) => err.dayMonthError)
        .filter((error) => error !== undefined) as number[];
    const entitiesWithoutDayValues = errorEntities.map((err) => err.dayError).filter((error) => error !== undefined) as number[];

    return { entitiesWithoutDayAndMonthValues, entitiesWithoutDayValues };
}

// Format data chunk into CreateFYIEntity[] format
export function formatEntitiesChunk(data: OnboardingFormEntry[]): CreateFYIEntity[] {
    return data.map((c) => {
        let businessStructure: EntityTypeEnum = EntityTypeEnum.OTHER;

        if (c.fyiEntity.details.businessStructure) {
            const type = c.fyiEntity.details.businessStructure.toUpperCase().replaceAll(' ', '_') as EntityTypeEnum;
            if (Object.values(EntityTypeEnum).includes(type)) {
                businessStructure = type;
            }
        }

        return {
            id: c.fyiEntity.id,
            name: c.fyiEntity.name,
            businessStructure,
            entityGroupId: c.fyiEntity.entityGroupId || undefined,
            financialYearEndDay: c.day as number,
            financialYearEndMonth: c.month as number,
        };
    });
}

// Format error messages object into a string array
export function formatErrorMessages(messages?: { [groupName: string]: string[] }): string[] {
    if (!messages) {
        return [];
    }

    const errorMessages: string[] = [];
    for (const key in messages) {
        for (const i in messages[key]) {
            errorMessages.push(`${key} : ${messages[key][i]}`);
        }
    }
    return errorMessages;
}

// Format success messages object into string array
export function formatSuccessMessages(data?: CreateFYIEntitiesResponse): string[] {
    if (!data) {
        return [];
    }

    const successMessages: string[] = [];
    const { entitiesOnboarded, existingEntitiesMapped } = data;

    if (entitiesOnboarded) {
        successMessages.push(`${entitiesOnboarded} new ${plural(entitiesOnboarded, 'entity', 'entities')} onboarded.`);
    }

    if (existingEntitiesMapped) {
        successMessages.push(`${existingEntitiesMapped} existing ${plural(existingEntitiesMapped, 'entity', 'entities')} mapped to FYI.`);
    }

    return successMessages;
}
